import { renderToStaticMarkup } from 'react-dom/server';
import { MemoryRouter } from 'react-router-dom';
import type { NostrEvent } from '@nostrify/nostrify';
import { nip19 } from 'nostr-tools';
import { BookOpen, ChevronDown, FileDown, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { WikiContent } from '@/components/WikiContent';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { detectContentFormat } from '@/lib/contentFormat';
import { absolutizeInternalLinks, buildStandaloneHtml, downloadTextFile, slugifyFilename } from '@/lib/export';
import { articleTitle } from '@/lib/nip54';
import {
  PUBLICATION_INDEX_KIND,
  getPublicationAuthors,
  getPublicationRefs,
  publicationSummary,
  publicationTitle,
  sectionTitle,
} from '@/lib/nkbip01';
import { cn } from '@/lib/utils';

/** Display title for any event that can appear in a publication's table of contents. */
function tocTitle(event: NostrEvent): string {
  if (event.kind === PUBLICATION_INDEX_KIND) return publicationTitle(event);
  if (event.kind === 30041) return sectionTitle(event);
  // 30817/30818/30023 leaves — NIP-54's title-or-d fallback covers them.
  return articleTitle(event);
}

interface PublicationHtmlBodyProps {
  index: NostrEvent;
  sections: NostrEvent[];
  authorNames: string;
}

/**
 * The full text of a publication as one document: a cover (title, summary,
 * byline), a linked table of contents, then every section in order with
 * its title as a heading. Rendered via React so every untrusted string is
 * auto-escaped; each section renders with its own detected format, since a
 * publication's sections aren't guaranteed to share one markup format.
 */
function PublicationHtmlBody({ index, sections, authorNames }: PublicationHtmlBodyProps) {
  const summary = publicationSummary(index);

  return (
    <>
      <header className="export-header">
        <h1>{publicationTitle(index)}</h1>
        {summary && <p>{summary}</p>}
        <p className="export-byline">
          {authorNames ? `by ${authorNames} · ` : ''}
          {new Date(index.created_at * 1000).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </header>

      {sections.length > 1 && (
        <nav className="export-toc">
          <h2>Contents</h2>
          <ol>
            {sections.map((section, i) => (
              <li key={section.id}>
                <a href={`#section-${i + 1}`}>{tocTitle(section)}</a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <main>
        {sections.map((section, i) => (
          <section key={section.id} className="export-section">
            <h1 id={`section-${i + 1}`}>{tocTitle(section)}</h1>
            {section.kind === PUBLICATION_INDEX_KIND ? (
              <p className="export-nested-note">
                This is a nested publication ({getPublicationRefs(section).length}{' '}
                {getPublicationRefs(section).length === 1 ? 'entry' : 'entries'}) — export it
                separately for its full contents.
              </p>
            ) : (
              <WikiContent content={section.content} format={detectContentFormat(section.content)} />
            )}
          </section>
        ))}
      </main>

      <footer className="export-footer">
        <p>
          Published as a signed Nostr event (kind {index.kind}) by {nip19.npubEncode(index.pubkey)} ·
          exported from {window.location.origin}
        </p>
      </footer>
    </>
  );
}

interface PublicationExportMenuProps {
  /** The publication index (kind 30040). */
  index: NostrEvent;
  /** The resolved sections, in table-of-contents order. Undefined while still loading. */
  sections: NostrEvent[] | undefined;
  sectionsLoading?: boolean;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * An "Export" dropdown for a whole publication:
 *
 *  - **Full HTML** — every section in one self-contained, styled document
 *    with a linked table of contents: a book as a single file, readable in
 *    any browser or e-reader (and a good input for print-to-PDF).
 *  - **Signed events** — the index plus all sections as one JSON bundle,
 *    signatures intact — a complete, verifiable backup of the publication.
 *
 * Both wait for sections to finish loading: a partial export that silently
 * drops chapters would be worse than a briefly-disabled menu item.
 */
export function PublicationExportMenu({
  index,
  sections,
  sectionsLoading,
  size = 'sm',
  className,
}: PublicationExportMenuProps) {
  const { toast } = useToast();
  const author = useAuthor(index.pubkey);
  const credited = getPublicationAuthors(index);
  const authorNames = credited.length > 0
    ? credited.map((a) => a.name).join(', ')
    : (author.data?.metadata?.name ?? '');

  const title = publicationTitle(index);
  const stem = slugifyFilename(title);
  const ready = sections !== undefined && !sectionsLoading;

  const exportHtml = () => {
    if (!sections) return;
    try {
      const bodyHtml = renderToStaticMarkup(
        <MemoryRouter>
          <PublicationHtmlBody index={index} sections={sections} authorNames={authorNames} />
        </MemoryRouter>,
      );
      const html = buildStandaloneHtml({
        title,
        description: publicationSummary(index),
        bodyHtml: absolutizeInternalLinks(bodyHtml, window.location.origin),
      });
      downloadTextFile(`${stem}.html`, html, 'text/html');
    } catch {
      toast({ title: 'Export failed', description: 'The HTML document could not be generated.', variant: 'destructive' });
    }
  };

  const exportJson = () => {
    if (!sections) return;
    downloadTextFile(`${stem}.json`, JSON.stringify({ index, sections }, null, 2), 'application/json');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size={size} className={cn('gap-1.5', className)} title="Export to file">
          <FileDown className="size-4" />
          Export
          <ChevronDown className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={exportHtml} disabled={!ready || (sections?.length ?? 0) === 0}>
          <BookOpen className="size-4" />
          Full book (.html)
          {!ready && ' — loading…'}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={exportJson} disabled={!ready}>
          <FileJson className="size-4" />
          Signed events (.json)
          {!ready && ' — loading…'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
